import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { QuantityPicker } from 'react-qty-picker';
import { Button, FormikWrapper } from '@uncinc/uncinc-react-kitchen-sink';
import Select from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/Select';

import { formatPrice } from '../../../helpers/commerce';
import WysiwygField from '../../WysiwygField';
import ProductBlock from '../ProductBlock';
import { ROUTE_CHECKOUT_V2, ROUTE_USER_CHECK } from '../../../config/routes';
import useProduct from '../../../hooks/useProduct';

import './style.scss';

// TODO: Renders field_commerce_product references. This duplicates the product
// purchase logic in implementations/Paragraph/Product/Default (ParagraphProduct):
// same useProduct + ProductBlock render. Unify the two into one shared component
// when there is time — kept separate for now to avoid risk. Note the quantity-
// counter rule differs (here: all available except a free e-learning; there:
// !isDigitalProduct) and must be reconciled when merging.
const GenericProduct = ({ entity }) => {
  const { t } = useTranslation();
  const loggedIn = useSelector((state) => state.getIn(['login', 'loggedIn'], false),
  );

  // Generic products keep their variations at the root level. The backend
  // normalizer exposes the CMS per-order cap under `maximum_quantity` (a
  // string); 500 acts as "no limit" when it isn't set.
  const variationForMax = entity.getIn(['variations', 0]);
  const maximumQuantity =
    Number(variationForMax?.get('maximum_quantity')) || 500;

  const {
    quantity,
    setQuantity,
    handleOrderClick,
    handleVariationChange,
    isLoading,
    title,
    variationPrice,
    hasMultipleVariations,
    body,
    isDigitalProduct,
    isAvailable,
    discountTiers,
    productImage,
  } = useProduct(entity, {
    entityPath: [], // Generic products have variations at root level
    maxQuantity: maximumQuantity,
    trackViewItem: false, // Generic products don't track view_item analytics
  });

  const isFree = Number.parseFloat(variationPrice) === 0;
  // A free e-learning is a registration ("Aanmelden"); a free physical product
  // is still ordered and shipped, so it keeps the normal "Bestellen" button.
  const isFreeRegistration = isFree && isDigitalProduct;

  const priceDisplay = isFree ? (
    t('cart.free')
  ) : (
    <div className="Product__Price__display">
      {t('cart.price_display', {
        price: formatPrice(variationPrice),
        interpolation: { escapeValue: true },
      })}
      <span>{t('cart.excl_btw')}</span>
    </div>
  );

  const toCartIconClasses = classNames('icon icon-variant-2', {
    'icon-right-arrow': isFreeRegistration,
    'icon-shopping-cart': !isFreeRegistration,
  });

  // Generate variation options for select
  const variations = entity.getIn(['variations']);
  const options =
    variations
      ?.map((_, index) => ({
        value: index,
        label: t('cart.variant_option', { number: index + 1 }),
      }))
      .toArray() || [];

  const getButtonHref = () => {
    if (!isDigitalProduct) return '#';
    return loggedIn ? ROUTE_CHECKOUT_V2 : ROUTE_USER_CHECK;
  };

  const variationSelect = hasMultipleVariations ? (
    <FormikWrapper>
      <Select
        required
        placeholder={t('cart.choose_variant')}
        fieldName="product_variation"
        options={options}
        onChange={handleVariationChange}
      />
    </FormikWrapper>
  ) : null;

  const orderButton = (
    <Button
      href={getButtonHref()}
      className="Button--primary Button--order"
      onClick={handleOrderClick}
      disabled={isLoading}
    >
      {isFreeRegistration ? t('cart.register') : t('cart.checkout')}
      <div className={toCartIconClasses} />
    </Button>
  );

  // Both paid and free available products use the same product card. Free
  // products show "Gratis" and omit the VAT note (isFree prop). The quantity
  // counter shows for every available product except a free e-learning, which
  // can only be registered for once (capped at one, so no counter). Unavailable
  // products fall through to the layout below.
  if (isAvailable) {
    return (
      <div className="Paragraph ParagraphCTA Product ParagraphCTA--variant--get-started">
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <ProductBlock
              productImage={productImage}
              title={title}
              description={body}
              price={variationPrice}
              isFree={isFree}
              discountTiers={discountTiers}
              quantity={quantity}
              variationSelect={variationSelect}
              orderBar={
                <>
                  {!isFreeRegistration && (
                    <QuantityPicker
                      min={1}
                      max={maximumQuantity}
                      value={quantity}
                      onChange={setQuantity}
                      width="2.9rem"
                    />
                  )}
                  {orderButton}
                </>
              }
            />
          </div>
        </div>
      </div>
    );
  }

  // Unavailable product: show the product info only — there is nothing to order.
  return (
    <div className="Paragraph ParagraphCTA Product ParagraphCTA--variant--get-started">
      <div className="ParagraphCTA__inner Product__inner">
        <div className="ParagraphCTA__body Product__body">
          <div className="row">
            <div className="col-12 col-lg-6">
              <h3 className="Product__title">{title}</h3>
              <WysiwygField
                className="Product__description subheading2"
                property="schema:text"
                html={body}
              />
            </div>
            <div
              className={classNames('Product__wrapper col-12 col-lg-6', {
                variant: hasMultipleVariations,
              })}
            >
              <h3 className="Product__Price">{priceDisplay}</h3>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenericProduct;
